import { useContext } from 'react'
import { CloseGuardContext } from '~/components/layout/GlobalFlyout'

/**
 * Hook to conditionally prevent a flyout from closing.
 *
 * @example
 * const registerGuard = useCloseGuard()
 *
 * useEffect(() => {
 *   if (hasError) {
 *     return registerGuard(async () => {
 *       const confirmed = await showConfirmation()
 *       return confirmed // true = allow close, false = prevent close
 *     })
 *   }
 * }, [hasError, registerGuard])
 */
export const useCloseGuard = () => {
  const context = useContext(CloseGuardContext)

  if (!context) {
    // If no context, we're not in a GlobalFlyout - return no-op
    return () => () => {}
  }

  const { guardRef } = context

  return (checkCanClose: () => Promise<boolean> | boolean) => {
    guardRef.current = checkCanClose

    // Return cleanup function
    return () => {
      guardRef.current = null
    }
  }
}
